import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiZap, FiLink, FiGlobe, FiCpu, FiCheck } from 'react-icons/fi';
import { motion } from 'framer-motion';

const ProductCard = ({ product, onAdd }) => {
  const [isDeploying, setIsDeploying] = useState(false);

  const getIcon = (category) => {
    switch(category) {
      case 'Power': return FiZap;
      case 'Cable': return FiLink;
      case 'Adapter': return FiGlobe;
      default: return FiPlus;
    }
  };

  const handleDeploy = () => {
    setIsDeploying(true);
    setTimeout(() => {
      setIsDeploying(false);
      onAdd();
    }, 800);
  };

  return (
    <motion.div 
      layout
      className="dashboard-panel group flex flex-col h-full focus-within:ring-2 focus-within:ring-electric/50 outline-none"
    >
      <div className="flex justify-between items-center px-4 py-3 border-b border-graphite-700 bg-graphite-900/50">
        <div className="flex items-center gap-2 fids-text text-[10px] text-gray-500 uppercase">
          <SafeIcon icon={getIcon(product.category)} className="text-electric" />
          {product.category}_MDL
        </div>
        <div className="flex items-center gap-2">
          <span className={`fids-text text-[10px] font-bold ${product.status === 'ONLINE' ? 'text-green-400' : 'text-yellow-400'}`}>
            {product.status}
          </span>
          <div className={`w-1.5 h-1.5 rounded-full ${product.status === 'ONLINE' ? 'bg-green-400' : 'bg-yellow-400'}`} />
        </div>
      </div>

      <div className="relative h-56 overflow-hidden bg-graphite-900">
        <img 
          src={product.image} 
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite-900 via-transparent to-transparent opacity-60" />
      </div>

      <div className="p-5 flex-grow">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-bold group-hover:text-electric transition-colors uppercase tracking-tight">{product.name}</h3>
        </div>
        <p className="text-sm text-gray-400 mb-6 h-10 line-clamp-2 fids-text text-[11px] leading-relaxed uppercase">{product.subtitle}</p>
        
        <div className="flex flex-wrap gap-1.5">
          {product.compatibility.map(chip => (
            <span key={chip} className="fids-text text-[9px] px-2 py-0.5 border border-graphite-700 bg-graphite-900 text-gray-500 rounded-sm">
              {chip}
            </span>
          ))}
        </div>
      </div>

      <div className="p-4 pt-0 flex items-center justify-between gap-4">
        <span className="fids-text text-lg font-bold">${product.price.toFixed(2)}</span>
        <button 
          onClick={handleDeploy}
          disabled={product.status === 'OFFLINE' || isDeploying}
          className={`flex-1 flex items-center justify-center gap-2 py-3 fids-text text-[10px] font-black border transition-all active:scale-95 ${
            product.status !== 'OFFLINE'
            ? 'border-electric text-electric hover:bg-electric hover:text-graphite-900'
            : 'border-graphite-700 text-gray-600 cursor-not-allowed'
          }`}
          aria-label={`Add ${product.name} to deployment`}
        >
          {isDeploying ? (
            <span className="animate-pulse">DEPLOYING...</span>
          ) : (
            <>
              <SafeIcon icon={FiCpu} />
              DEPLOY_MODULE
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default ProductCard;